import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { limitShortfall } from 'fym-shared/src/music24/limitUpsell';
import { TypedMemo } from '../../../../../../common/types';
import type { ReleasePost } from '../../../../../../../redux/api/fymAPI.types';
import { LimitUpsell } from '../../../../../limitUpsell/LimitUpsell';
import { useRollout } from '../RolloutContext';
import { launchMessages } from './launchMessages';
import { draftsThatFitNow, monthLimitPosts } from './launchModel';

interface ScheduleAllUpsellProps {
  /** Every draft of the plan ("Schedule all N posts"). */
  drafts: ReleasePost[];
  /** `GET promo/quota` of the workspace; null while it loads. */
  quota: { used: number; limit: number | null } | null;
}

/**
 * The limit upsell of "Schedule all" (design limit-upsell a1, a11). Before the click: the drafts
 * need more posts than this month has left (every post takes its unit in the month of the click).
 * After the click: the server left posts as drafts for `monthLimit`; an add-on schedules them
 * again. "Schedule only what fits" schedules the first drafts in time order that fit.
 */
const ScheduleAllUpsellComponent: React.FC<ScheduleAllUpsellProps> = ({ drafts, quota }) => {
  const intl = useIntl();
  const { actions, canAct } = useRollout();
  const { launch, launchOutcome, busy } = actions;
  // The drafts whose time is past never go out; the time of the first render is good enough.
  const [now] = useState(() => Date.now());

  const open = useMemo(() => drafts.filter((post) => Date.parse(post.scheduledAt) >= now), [drafts, now]);
  const left = quota === null || quota.limit === null ? null : Math.max(0, quota.limit - quota.used);
  const refused = useMemo(() => monthLimitPosts(launchOutcome), [launchOutcome]);

  const before = useMemo(
    () => (refused.length > 0 || busy !== null ? null : limitShortfall('posts', open.length, left)),
    [busy, left, open.length, refused.length]
  );
  const after = useMemo(
    () => (refused.length === 0 ? null : limitShortfall('posts', refused.length + (left ?? 0), left ?? 0)),
    [left, refused.length]
  );

  const fitting = useMemo(() => draftsThatFitNow(open, left ?? 0), [left, open]);
  const fit = useMemo(
    () =>
      fitting.length === 0
        ? null
        : {
            label: intl.formatMessage(launchMessages.scheduleWhatFits, { count: fitting.length }),
            onClick: () => {
              launch('plan', fitting).catch(console.error);
            },
          },
    [fitting, intl, launch]
  );
  const scheduleRefused = useCallback(() => {
    launch('plan', refused).catch(console.error);
  }, [launch, refused]);

  if (!canAct) {
    return null;
  }
  return (
    <>
      <LimitUpsell shortfall={before} context="plan" fit={fit} />
      <LimitUpsell shortfall={after} context="refused" onAdded={scheduleRefused} />
    </>
  );
};

export const ScheduleAllUpsell = TypedMemo(ScheduleAllUpsellComponent);
